/**
 * Dictation, local only (privacy fix, VC8). Chrome's `webkitSpeechRecognition` sends audio to
 * Google unless it runs with `processLocally`; Safari's sends it to Apple. So dictation uses the
 * same Local path as Viko Call (lib/call/local-speech.ts):
 * - the browser's on-device recognizer when it is guaranteed local (`processLocally`), with
 *   interim text as you speak, or
 * - your own Mac's whisper: the mic records in memory, the audio is sealed to your Mac, and the
 *   text arrives when you stop.
 * Neither available: `supported: false` and the mic button hides. Never a cloud recognizer.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  chooseSpeechPath,
  type LocalRecognition,
  localRecognitionClass,
  type SpeechPathChoice,
  speechLang,
  startLocalRecognition,
} from './call/local-speech.js'
import type { CallMac } from './call/mac.js'
import { useMacSpeech } from './call/mac-context.js'
import { type PcmRecorder, recordPcm16, transcribeOnMac } from './call/mac-whisper.js'
import { currentEnv, inAppDictationUsable } from './ios-pwa.js'

/** Kept for voice mode's typing: the recognizer shape (now only ever local). */
export type { LocalRecognition as Recognition } from './call/local-speech.js'

/** The on-device recognizer constructor, or null (never a cloud-backed one). */
export function ctor() {
  return localRecognitionClass() ?? null
}

/** Shared choice per (machine) so every mic button agrees and `available()` runs once. */
let cached: { key: string; choice: Promise<SpeechPathChoice> } | undefined
export function speechPathFor(mac: CallMac | undefined): Promise<SpeechPathChoice> {
  const key = mac?.id ?? '-'
  if (cached?.key !== key) cached = { key, choice: chooseSpeechPath({ mac }) }
  return cached.choice
}
/** Tests: forget the cached choice. */
export function resetSpeechPath() {
  cached = undefined
}

export function useDictation(onFinal: (text: string) => void) {
  // Optional: without the app shell (isolated renders) only on-device speech is offered.
  const macSpeech = useMacSpeech()
  const mac = macSpeech?.mac
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [choice, setChoice] = useState<SpeechPathChoice | undefined>()
  const rec = useRef<LocalRecognition | null>(null)
  const recorder = useRef<PcmRecorder | null>(null)
  const final = useRef(onFinal)
  final.current = onFinal

  // No probing on render: `available()` runs when you press the mic (it can be slow, and some
  // embedded Chromium builds crash on it). Shown when a local path could exist.
  // ios-pwa: Home Screen apps have no in-app recognition; the keyboard's mic is the path.
  const supported = (!!localRecognitionClass() || !!mac) && inAppDictationUsable(currentEnv())

  const stop = useCallback(() => {
    rec.current?.stop()
    const r = recorder.current
    if (!r || !macSpeech || choice?.path !== 'mac-whisper') return
    recorder.current = null
    setInterim('Transcribing on your Mac…')
    void r
      .stop()
      .then((pcm) =>
        pcm.length
          ? transcribeOnMac(pcm, {
              callId: crypto.randomUUID(),
              utteranceId: `d${Date.now().toString(36)}`,
              dictation: true,
              send: async (payload) => macSpeech.send(payload),
            })
          : '',
      )
      .then((text) => {
        if (text) final.current(text)
      })
      .catch((e: unknown) =>
        setError(`Transcription on your Mac failed${e instanceof Error ? `: ${e.message}` : ''}.`),
      )
      .finally(() => {
        setListening(false)
        setInterim('')
      })
  }, [macSpeech, choice])

  const start = useCallback(async () => {
    setError(null)
    const choice = await speechPathFor(mac)
    setChoice(choice)
    if (choice.path === 'none')
      return setError('Voice input needs on-device speech or your Mac. Type instead.')
    if (choice.path === 'browser-local') {
      const C = localRecognitionClass()
      if (!C) return setError('On-device speech is not available here.')
      try {
        rec.current = startLocalRecognition(C, speechLang(), {
          onInterim: setInterim,
          onFinal: (t) => {
            if (t) final.current(t)
          },
          onError: (e) =>
            setError(e.reason === 'not-allowed' ? 'Microphone access was denied.' : `${e.message}`),
          onEnd: () => {
            setListening(false)
            setInterim('')
          },
        })
        setListening(true)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'On-device speech is not available here.')
      }
      return
    }
    void recordPcm16({ onAutoStop: () => stop() })
      .then((r) => {
        recorder.current = r
        setListening(true)
        setInterim('Listening… stop to transcribe on your Mac')
      })
      .catch(() => setError('Microphone access was denied.'))
  }, [mac, stop])

  useEffect(
    () => () => {
      rec.current?.abort()
      recorder.current?.cancel()
    },
    [],
  )

  return {
    supported,
    listening,
    interim,
    error,
    /** Where the audio goes: "on-device speech" / "whisper on Mac Studio". */
    path: choice,
    start,
    stop,
    toggle: () => (listening ? stop() : void start()),
  }
}
